import { useEffect, useState } from 'react';
import HeroCarousel from '../components/HeroCarousel';
import defaultCarouselSlides from '../data/carouselImages';
import spBafang from '../assets/images/spbafang.png';
import spBana from '../assets/images/spbana.jpg';
import spCanal from '../assets/images/spcanal.jpg';
import spCrtv from '../assets/images/spcrtv.jpeg';
import spFecafoot from '../assets/images/spfecafoot.jpg';
import spMufid from '../assets/images/spmufid.webp';
import logouhk from '../assets/images/logouhk.png';
import logoSansBg from '../assets/images/logosansbg.png';
import defaultNewsCards, { sanitizeNewsCards } from '../data/newsDefaults';
import { fetchClubInfo, fetchNextMatch } from '../services/api';
import {
  sendSponsorAcknowledgement,
  sendMembershipAcknowledgement,
  notifyAdminsMembershipRequest,
  sendAdhesionAcknowledgement,
  notifyAdminsAdhesionRequest
} from '../services/emailService';

const defaultMatchInfo = {
  homeTeam: 'Unisport du Haut-Nkam',
  awayTeam: 'Fovu Club de Baham',
  score: 'Unisport du Haut-Nkam 3 - 0 Fovu Club de Baham',
  competition: 'Elite One',
  league: 'LIGUE 1 Elite One',
  dateInfo: 'Dimanche 12 Janvier 2025, 20h45',
  location: 'Stade Municipal de Bafang',
  playedAt: 'Dimanche 12 Janvier 2025',
  homeLogo: logouhk,
  awayLogo: logouhk
};

const defaultNextMatch = {
  league: 'LIGUE 1 Elite One',
  dateInfo: 'Dimanche 30 Mars 2025, 20h45',
  stadium: 'Stade Municipal de Bafang',
  homeTeam: 'Unisport du Haut-Nkam',
  homeLogo: logouhk,
  awayTeam: 'Fovu Club de Baham',
  awayLogo: logouhk
};

const defaultPartners = [
  { name: 'SP Bafang', logo: spBafang },
  { name: 'SP Bana', logo: spBana },
  { name: 'SP Canal+', logo: spCanal },
  { name: 'SP CRTV', logo: spCrtv },
  { name: 'SP Fecafoot', logo: spFecafoot },
  { name: 'SP Mufid', logo: spMufid }
];

const membershipOptions = [
  { value: '50', label: 'Standard – 50 €' },
  { value: '100', label: 'Premium – 100 €' },
  { value: '200', label: 'VIP – 200 €' }
];

const defaultHistoryText =
  "Fondé le 2 octobre 1958, Unisport du Haut-Nkam accompagne les habitants de Bafang et de toute la diaspora camerounaise. Le club porte les couleurs rouge et jaune comme symbole d’unité, de courage et de tradition. Grâce à l’interface administrateur, vous pourrez enrichir cette rubrique au fil des saisons avec des anecdotes, des trophées et des témoignages.";

const escapeRegExp = (text = '') => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getScoreDisplay = (score = '', homeTeam = '', awayTeam = '') => {
  if (!score) return '0 - 0';
  let sanitized = score;
  [homeTeam, awayTeam].forEach((team) => {
    if (team) {
      const matcher = new RegExp(escapeRegExp(team), 'gi');
      sanitized = sanitized.replace(matcher, ' ');
    }
  });
  const scorePattern = /(\d+\s*(?:[-–:])\s*\d+)/;
  const match = sanitized.match(scorePattern);
  if (match) {
    return match[1].replace(/\s+/g, ' ').trim();
  }
  return sanitized.trim() || '0 - 0';
};

const dataUriToBlobUrl = (dataURI) => {
  if (!dataURI) return '';
  const parts = dataURI.split(',');
  if (parts.length < 2) return '';
  const byteString = atob(parts[1]);
  const mimeString = parts[0].split(':')[1]?.split(';')[0] || 'application/octet-stream';
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeString });
  return URL.createObjectURL(blob);
};

const normalizeAlbumPhotos = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') {
        return { src: item, title: '' };
      }
      if (item && typeof item === 'object' && typeof item.src === 'string') {
        return { src: item.src, title: item.title || '' };
      }
      return null;
    })
    .filter(Boolean);
};

const toDigitsOnly = (value = '') => value.replace(/\D/g, '');

const buildCarouselSlides = (slides = []) => {
  if (!Array.isArray(slides) || !slides.length) {
    return [...defaultCarouselSlides];
  }
  const fallbackSlides = [...defaultCarouselSlides];
  const normalized = slides
    .map((slide, index) => {
      if (!slide) return null;
      const media = typeof slide === 'string' ? slide : slide.image || slide.src;
      if (!media) return null;
      const fallback = fallbackSlides[index % fallbackSlides.length] || fallbackSlides[0];
      const headline =
        typeof slide.headline === 'string' && slide.headline.trim() ? slide.headline : fallback?.headline;
      const subline =
        typeof slide.subline === 'string' && slide.subline.trim() ? slide.subline : fallback?.subline;
      return {
        id: slide.id || slide._id || fallback?.id || `hero-${index}`,
        image: media,
        headline: headline || 'Unisport du Haut-Nkam',
        subline: subline || ''
      };
    })
    .filter(Boolean);
  return normalized.length ? normalized : [...defaultCarouselSlides];
};

export default function Home() {
  const [calendar, setCalendar] = useState('');
  const [calendarPdf, setCalendarPdf] = useState('');
  const [calendarPdfUrl, setCalendarPdfUrl] = useState('');
  const [albumModalOpen, setAlbumModalOpen] = useState(false);
  const [albumIndex, setAlbumIndex] = useState(0);
  const [albumCounter, setAlbumCounter] = useState('0/0');
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [newsCards, setNewsCards] = useState([...defaultNewsCards]);
  const [partnerLogos, setPartnerLogos] = useState([...defaultPartners]);
  const [expressNews, setExpressNews] = useState('');
  const [expandedNews, setExpandedNews] = useState(() => newsCards.map(() => false));
  const albumCount = albumPhotos.length;
  const [heroSlides, setHeroSlides] = useState([...defaultCarouselSlides]);
  const [matchInfo, setMatchInfo] = useState(defaultMatchInfo);
  const [nextMatchInfo, setNextMatchInfo] = useState(defaultNextMatch);
  const [historyText, setHistoryText] = useState(defaultHistoryText);
  const [membershipModal, setMembershipModal] = useState(false);
  const [membershipPhoto, setMembershipPhoto] = useState(null);
  const MAX_MEMBERSHIP_PHOTO_SIZE = 4 * 1024 * 1024;
  const [adhesionModal, setAdhesionModal] = useState(false);
  const [sponsorModal, setSponsorModal] = useState(false);
  const [membershipForm, setMembershipForm] = useState({
    civility: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
    membershipType: ''
  });
  const [adhesionForm, setAdhesionForm] = useState({
    civility: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: ''
  });
  const [sponsorForm, setSponsorForm] = useState({
    company: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    country: ''
  });

  const handleMembershipPhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setMembershipPhoto(null);
      return;
    }
    if (file.size > MAX_MEMBERSHIP_PHOTO_SIZE) {
      alert('La photo doit peser moins de 4 Mo.');
      event.target.value = '';
      setMembershipPhoto(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const base64 = result.includes('base64,') ? result.split('base64,')[1] : result;
        setMembershipPhoto({
          data: base64,
          name: file.name,
          type: file.type || 'image/jpeg'
        });
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    fetchClubInfo()
      .then((data) => {
        setHistoryText(data.history || defaultHistoryText);
        const nextCards =
          data.fanionNews && data.fanionNews.length ? sanitizeNewsCards(data.fanionNews) : [...defaultNewsCards];
        setNewsCards(
          nextCards.map((card, index) => ({
            ...card,
            order: typeof card.order === 'number' ? card.order : index
          }))
        );
        setExpandedNews(nextCards.map(() => false));
        setExpressNews(data.expressNews || '');
        if (data.lastMatch) {
          setMatchInfo((prev) => ({ ...prev, ...data.lastMatch }));
        }
        if (typeof data.calendarText === 'string') {
          setCalendar(data.calendarText);
        }
        if (data.calendarPdf) {
          setCalendarPdf(data.calendarPdf);
        }
        if (Array.isArray(data.albumPhotos)) {
          setAlbumPhotos(normalizeAlbumPhotos(data.albumPhotos));
        } else {
          setAlbumPhotos([]);
        }
        if (Array.isArray(data.partnerLogos) && data.partnerLogos.length) {
          setPartnerLogos(data.partnerLogos);
        } else {
          setPartnerLogos([...defaultPartners]);
        }
        if (Array.isArray(data.carouselImages) && data.carouselImages.length) {
          setHeroSlides(buildCarouselSlides(data.carouselImages));
        } else {
          setHeroSlides([...defaultCarouselSlides]);
        }
      })
      .catch(() => {
        setHistoryText(defaultHistoryText);
        setNewsCards([...defaultNewsCards]);
        setHeroSlides([...defaultCarouselSlides]);
      });
  }, []);

  useEffect(() => {
    fetchNextMatch()
      .then((data) => setNextMatchInfo({ ...defaultNextMatch, ...data }))
      .catch(() => setNextMatchInfo(defaultNextMatch));
  }, []);

  useEffect(() => {
    if (!calendarPdf) {
      setCalendarPdfUrl('');
      return undefined;
    }
    const url = dataUriToBlobUrl(calendarPdf);
    setCalendarPdfUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [calendarPdf]);

  const homeLogo = matchInfo.homeLogo || logouhk;
  const awayLogo = matchInfo.awayLogo || logouhk;
  const nextHomeLogo = nextMatchInfo.homeLogo || logouhk;
  const nextAwayLogo = nextMatchInfo.awayLogo || logouhk;
  const scoreDisplay = getScoreDisplay(matchInfo.score, matchInfo.homeTeam, matchInfo.awayTeam);
  useEffect(() => {
    setExpandedNews((prev) => newsCards.map((_, index) => prev[index] || false));
  }, [newsCards]);

  useEffect(() => {
    if (!albumPhotos.length) {
      setAlbumIndex(0);
      setAlbumCounter('0/0');
      return;
    }
    setAlbumIndex((prev) => Math.min(prev, albumPhotos.length - 1));
  }, [albumPhotos]);

  useEffect(() => {
    if (!albumCount) {
      setAlbumCounter('0/0');
      return;
    }
    setAlbumCounter(`${albumIndex + 1}/${albumCount}`);
  }, [albumIndex, albumCount]);

  const openAlbumModal = () => {
    setAlbumIndex(0);
    setAlbumModalOpen(true);
  };

  const handleAlbumNav = (direction) => {
    if (!albumPhotos.length) return;
    setAlbumIndex((prev) => {
      const total = albumPhotos.length;
      if (direction === 'next') {
        return (prev + 1) % total;
      }
      return (prev - 1 + total) % total;
    });
  };

  return (
    <>
      <section className="hero-section">
        <HeroCarousel slides={heroSlides} />
        <div className="hero-section__ticker">
          <p>🔥 Boutique MPA’CHI — Livraison à travers le monde — Paiement via PayPal • Wero • Mobile Money</p>
          <div className="hero-section__socials">
            <a href="https://www.facebook.com/people/Unisport-Europe-Officiel/61579959819674/?_rdr" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M15 3h3V0h-3c-3.3 0-6 2.7-6 6v3H6v4h3v11h4V13h3l1-4h-4V6c0-.6.4-1 1-1z"
                />
              </svg>
            </a>
            <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M16 3H8C5.2 3 3 5.2 3 8v8c0 2.8 2.2 5 5 5h8c2.8 0 5-2.2 5-5V8c0-2.8-2.2-5-5-5zm3 13c0 1.7-1.3 3-3 3H8c-1.7 0-3-1.3-3-3V8c0-1.7 1.3-3 3-3h8c1.7 0 3 1.3 3 3v8z"
                />
                <path
                  fill="currentColor"
                  d="M12 8.5A3.5 3.5 0 1 0 12 15a3.5 3.5 0 0 0 0-6.5zm0 5A1.5 1.5 0 1 1 12 10a1.5 1.5 0 0 1 0 3zm4.3-6.7a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/watch?v=hhtLnODEu7k&list=RDhhtLnODEu7k&start_radio=1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <svg viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M23 7.2s-.2-1.6-.8-2.3c-.7-.8-1.5-.8-1.9-.9C17.5 3.5 12 3.5 12 3.5h0s-5.5 0-8.3.5c-.4.1-1.2.1-1.9.9C1.2 5.6 1 7.2 1 7.2S.8 9.1.8 11v2c0 1.9.2 3.8.2 3.8s.2 1.6.8 2.3c.7.8 1.6.8 2 1 1.5.1 8.2.5 8.2.5s5.5 0 8.3-.5c.4-.1 1.2-.1 1.9-.9.6-.7.8-2.3.8-2.3s.2-1.9.2-3.8V11c0-1.9-.2-3.8-.2-3.8zM10 15.5v-7l6 3.5-6 3.5z"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>
      <section className="next-match-banner">
        <div className="next-match-banner__content">
          <div className="next-match-banner__info">
            <p className="next-match-banner__tagline">{nextMatchInfo.league}</p>
            <h2>PROCHAIN MATCH</h2>
            <p className="next-match-banner__details">
              {nextMatchInfo.dateInfo} — {nextMatchInfo.stadium}
            </p>
          </div>
          <div className="next-match-banner__teams">
            <div className="next-match-banner__team next-match-banner__team--home" translate="no">
              <img src={nextHomeLogo} alt={nextMatchInfo.homeTeam} />
              <span>{nextMatchInfo.homeTeam}</span>
            </div>
            <div className="next-match-banner__vs" translate="no">
              VS
            </div>
            <div className="next-match-banner__team next-match-banner__team--away" translate="no">
              <img src={nextAwayLogo} alt={nextMatchInfo.awayTeam} />
              <span>{nextMatchInfo.awayTeam}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="match-result" aria-labelledby="last-match">
        <div className="match-result__card">
          <header>
            <p>Dernier match</p>
            <div className="match-result__logos">
              <div className="match-result__team match-result__team--home" translate="no">
                <img src={homeLogo} alt={matchInfo.homeTeam} />
                <p>{matchInfo.homeTeam}</p>
              </div>
              <div className="match-result__score-badge">
                <span>{scoreDisplay}</span>
              </div>
              <div className="match-result__team match-result__team--away" translate="no">
                <img src={awayLogo} alt={matchInfo.awayTeam} />
                <p>{matchInfo.awayTeam}</p>
              </div>
            </div>
          </header>
         <ul>
            <li>{matchInfo.league || matchInfo.competition}</li>
            <li>{matchInfo.dateInfo || matchInfo.playedAt}</li>
            <li>{matchInfo.location || matchInfo.stadium}</li>
          </ul>
          <a href="#calendrier" className="match-result__cta">Voir le Calendrier</a>
        </div>
        <div className="match-result__news">
          <h3>Actualités express</h3>
          <p>{expressNews || "Le tableau de bord administrateur vous permettra de publier les actualités, interviews et résultats."}</p>
          <a href="#actu">Toutes les actualités</a>
        </div>
      </section>

      <section className="history-section" id="histoire">
        <div className="history-section__content">
          <h2>Notre histoire</h2>
          <p>{historyText}</p>
          <p>Envie de participer à la rédaction? Rejoignez le programme supporters premium.</p>
          <div className="history-section__actions">
            <button type="button" className="history-section__album-button" onClick={openAlbumModal}>
              ALBUM
            </button>
          </div>
        </div>
      </section>

      <section className="news-section" id="actu">
        <header>
          <h2>Actualités</h2>
          <p>Coulisses, mercato : tout ce qui fait vibrer les Fans Unisport du Haut-Nkam.</p>
        </header>
        <div className="news-grid">
          {newsCards.map((card, index) => {
            const isExpanded = expandedNews[index];
            return (
              <article key={`news-${index}`}>
                <p className="category">Équipe fanion</p>
                <h3>{card.title || `Fait marquant #${index + 1}`}</h3>
                <p className={`news-card__content ${isExpanded ? 'is-expanded' : ''}`}>{card.content}</p>
                <button
                  type="button"
                  onClick={() =>
                    setExpandedNews((prev) => {
                      const updated = [...prev];
                      updated[index] = !updated[index];
                      return updated;
                    })
                  }
                >
                  {isExpanded ? 'Réduire' : 'Lire +'}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="calendar-section" id="calendrier">
        <h2>Calendrier des matchs</h2>
        <p>Prochains rendez-vous programmés pour Unisport du Haut-Nkam.</p>
        {calendar ? (
          <>
            <table className="calendar-table">
              <tbody>
                {calendar
                  .split('\n')
                  .filter(Boolean)
                  .map((row) => {
                    const [date = '', ...rest] = row.split('-');
                    const info = rest.length ? rest.join('-') : '';
                    return (
                      <tr key={`${date}-${info}`}>
                        <td>{date.trim()}</td>
                        <td>{info.trim()}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {calendarPdfUrl && (
              <div className="calendar-button-wrapper">
                <a
                  href={calendarPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-header__cta site-header__cta--ghost calendar-button"
                >
                  Voir plus
                </a>
              </div>
            )}
          </>
        ) : (
          <p>Le calendrier sera bientôt publié par l’administrateur.</p>
        )}
      </section>
      <section className="partners-section" id="partenaires">
        <header>
          <h2>Partenaires & Offres</h2>
          <p>Rejoignez le cercle des partenaires officiels de Unisport du Haut-Nkam.</p>
        </header>
        <div className="partners-logos">
          {partnerLogos.map((partner, index) => (
            <div key={partner.name} className="partner-card">
              <img src={partner.logo} alt={partner.name || `Sponsor ${index + 1}`} />
            </div>
          ))}
        </div>
        <div className="partners-actions">
          <button type="button" className="site-header__cta" onClick={() => setSponsorModal(true)}>
            DEVENIR SPONSOR
          </button>
          <button type="button" className="site-header__cta site-header__cta--ghost" onClick={() => setMembershipModal(true)}>
            DEVENIR MEMBRE
          </button>
          <button type="button" className="site-header__cta site-header__cta--ghost" onClick={() => setAdhesionModal(true)}>
            DEVENIR ADHÉRENT(E)
          </button>
        </div>
      </section>

      {sponsorModal && (
        <div className="membership-modal">
          <div className="membership-modal__content">
            <button type="button" className="membership-modal__close" onClick={() => setSponsorModal(false)}>
              ×
            </button>
            <h3>Devenir sponsor</h3>
            <div className="membership-modal__grid">
              <div className="membership-info">
                <p>Devenez sponsor d’Unisport du Haut-Nkam et associez votre entreprise au dynamisme du club !</p>
                <p>
                  Nous recherchons des partenaires engagés pour accompagner le développement de notre équipe fanion et
                  renforcer son impact sportif et communautaire. En devenant sponsor, vous bénéficiez d’une visibilité
                  privilégiée et de nombreux avantages personnalisables selon vos objectifs :
                </p>
                <ul>
                  <li>Présence de votre marque sur nos supports de communication (maillots, panneaux, réseaux sociaux…)</li>
                  <li>Accès privilégié aux matchs et places en carré VIP à Bafang</li>
                  <li>Opportunités promotionnelles via notre boutique et nos événements</li>
                  <li>Visibilité régionale et nationale grâce aux performances du club</li>
                </ul>
                <p>
                  Rejoignez la grande famille d’Unisport du Haut-Nkam et développez votre notoriété en soutenant notre projet
                  sportif et communautaire. Ensemble, faisons rayonner le Haut-Nkam !
                </p>
              </div>
              <form
                className="membership-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  const company = sponsorForm.company.trim();
                  const email = sponsorForm.email.trim();
                  const phone = sponsorForm.phone.trim();
                  const street = sponsorForm.street.trim();
                  const city = sponsorForm.city.trim();
                  const postalCode = sponsorForm.postalCode.trim();
                  const country = sponsorForm.country.trim();

                  if (!company || !email || !phone || !street || !city || !postalCode || !country) {
                    alert('Veuillez renseigner tous les champs.');
                    return;
                  }

                  try {
                    await sendSponsorAcknowledgement({ company, email, phone, street, city, postalCode, country });
                    setSponsorModal(false);
                  } catch (error) {
                    // L'alerte est gérée dans sendSponsorAcknowledgement
                  }
                }}
              >
                <label>
                  Nom de l’entreprise <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={sponsorForm.company}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, company: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Courriel <span className="required-marker">*</span>
                  <input
                    type="email"
                    value={sponsorForm.email}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, email: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Numéro de téléphone <span className="required-marker">*</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={sponsorForm.phone}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, phone: toDigitsOnly(event.target.value) }))}
                    required
                  />
                </label>
                <label>
                  Numéro et nom de la voie <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={sponsorForm.street}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, street: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ville <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={sponsorForm.city}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, city: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Code postal <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={sponsorForm.postalCode}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, postalCode: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Pays <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={sponsorForm.country}
                    onChange={(event) => setSponsorForm((prev) => ({ ...prev, country: event.target.value }))}
                    required
                  />
                </label>
                <button type="submit" className="match-result__cta">
                  Envoyer la demande
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {membershipModal && (
        <div className="membership-modal">
          <div className="membership-modal__content">
            <button
              type="button"
              className="membership-modal__close"
              onClick={() => {
                setMembershipModal(false);
                setMembershipPhoto(null);
              }}
            >
              ×
            </button>
            <h3>Devenir membre</h3>
            <div className="membership-modal__grid">
              <div className="membership-info">
                <p>Soutenez l’équipe fanion d’Unisport du Haut-Nkam en devenant membre !</p>
                <p>Rejoignez notre grande famille et contribuez au rayonnement de votre club. Choisissez la formule qui vous convient :</p>
                <ul>
                  <li>Carte Standard – 50 €</li>
                  <li>Carte Premium – 100 €</li>
                  <li>Carte VIP – 200 €</li>
                </ul>
                <p>Les cartes Premium et VIP vous offrent de nombreux avantages exclusifs, notamment :</p>
                <ul>
                  <li>Des places en carré VIP pour tous les matchs à Bafang</li>
                  <li>Des réductions sur notre boutique en ligne</li>
                  <li>De nombreux privilèges réservés à nos membres fidèles</li>
                </ul>
                <p>Rejoignez-nous dès aujourd’hui et vivez votre passion au plus près du club !</p>
                <img src={logoSansBg} alt="Logo Unisport" className="membership-info__logo" />
              </div>
              <form
                className="membership-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (
                    !membershipForm.civility ||
                    !membershipForm.lastName.trim() ||
                    !membershipForm.firstName.trim() ||
                    !membershipForm.email.trim() ||
                    !membershipForm.membershipType ||
                    !membershipForm.phone.trim() ||
                    !membershipForm.city.trim() ||
                    !membershipForm.country.trim()
                  ) {
                    alert('Veuillez renseigner tous les champs obligatoires.');
                    return;
                  }
                  const selectedMembership = membershipOptions.find(
                    (option) => option.value === membershipForm.membershipType
                  );
                  const membershipLabel = selectedMembership
                    ? selectedMembership.label
                    : `${membershipForm.membershipType} €`;
                  const payload = {
                    civility: membershipForm.civility,
                    firstName: membershipForm.firstName.trim(),
                    lastName: membershipForm.lastName.trim(),
                    email: membershipForm.email.trim(),
                    phone: membershipForm.phone.trim(),
                    membershipType: membershipForm.membershipType,
                    membershipLabel,
                    street: membershipForm.street.trim(),
                    city: membershipForm.city.trim(),
                    postalCode: membershipForm.postalCode.trim(),
                    country: membershipForm.country.trim(),
                    photo: membershipPhoto?.data || '',
                    photoName: membershipPhoto?.name || '',
                    photoType: membershipPhoto?.type || ''
                  };
                  try {
                    await sendMembershipAcknowledgement(payload);
                    notifyAdminsMembershipRequest(payload);
                    setMembershipModal(false);
                  } catch (error) {
                    // alert déjà gérée dans sendMembershipAcknowledgement
                  }
                }}
              >
                <label>
                  Civilité <span className="required-marker">*</span>
                  <select
                    value={membershipForm.civility}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, civility: event.target.value }))}
                    required
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mme">Mme</option>
                    <option value="NP">Ne souhaite pas préciser</option>
                  </select>
                </label>
                <label>
                  Nom <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={membershipForm.lastName}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, lastName: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Prénom <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={membershipForm.firstName}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, firstName: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Adresse e-mail <span className="required-marker">*</span>
                  <input
                    type="email"
                    value={membershipForm.email}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, email: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Numéro de téléphone
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={membershipForm.phone}
                    onChange={(event) =>
                      setMembershipForm((prev) => ({ ...prev, phone: toDigitsOnly(event.target.value) }))
                    }
                    required
                  />
                </label>
                <label>
                  Membre (choix) <span className="required-marker">*</span>
                  <select
                    value={membershipForm.membershipType}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, membershipType: event.target.value }))}
                    required
                  >
                    <option value="">Sélectionnez une formule</option>
                    {membershipOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Ville <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={membershipForm.city}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, city: event.target.value }))}
                    required
                  />
                </label>
              <label>
                Nom et numéro de voie
                <input
                  type="text"
                  value={membershipForm.street}
                  onChange={(event) => setMembershipForm((prev) => ({ ...prev, street: event.target.value }))}
                />
              </label>
                <label>
                  Code postal
                  <input
                    type="text"
                    value={membershipForm.postalCode}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, postalCode: event.target.value }))}
                  />
                </label>
                <label>
                  Pays <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={membershipForm.country}
                    onChange={(event) => setMembershipForm((prev) => ({ ...prev, country: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Photo pour la carte de membre (max 4 Mo)
                  <input type="file" accept="image/*" onChange={handleMembershipPhotoChange} />
                  {membershipPhoto?.name && <small>Fichier sélectionné : {membershipPhoto.name}</small>}
                </label>
              <button type="submit" className="match-result__cta">
                Envoyer la demande
              </button>
            </form>
            </div>
          </div>
        </div>
      )}

      {adhesionModal && (
        <div className="membership-modal">
          <div className="membership-modal__content">
            <button type="button" className="membership-modal__close" onClick={() => setAdhesionModal(false)}>
              ×
            </button>
            <h3>Devenir adhérent</h3>
            <div className="membership-modal__grid">
              <div className="membership-info">
                <p>Devenez adhérent de l’équipe fanion d’Unisport du Haut-Nkam !</p>
                <p>Carte adhérent – 20 €</p>
                <p>Rejoignez-nous dès aujourd’hui et vivez votre passion au plus près du club !</p>
                <img src={logoSansBg} alt="Logo Unisport du Haut-Nkam" className="membership-info__logo" />
              </div>

              <form
                className="membership-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (
                    !adhesionForm.civility ||
                    !adhesionForm.lastName.trim() ||
                    !adhesionForm.firstName.trim() ||
                    !adhesionForm.email.trim() ||
                    !adhesionForm.phone.trim()
                  ) {
                    alert('Veuillez renseigner tous les champs obligatoires.');
                    return;
                  }
                  const payload = {
                    civility: adhesionForm.civility,
                    firstName: adhesionForm.firstName.trim(),
                    lastName: adhesionForm.lastName.trim(),
                    email: adhesionForm.email.trim(),
                    phone: adhesionForm.phone.trim(),
                    street: adhesionForm.street.trim(),
                    city: adhesionForm.city.trim(),
                    postalCode: adhesionForm.postalCode.trim()
                  };
                  try {
                    await sendAdhesionAcknowledgement(payload);
                    notifyAdminsAdhesionRequest(payload);
                    setAdhesionModal(false);
                  } catch (error) {
                    // alert déjà gérée dans sendAdhesionAcknowledgement
                  }
                }}
              >
                <label>
                  Civilité <span className="required-marker">*</span>
                  <select
                    value={adhesionForm.civility}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, civility: event.target.value }))}
                    required
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mme">Mme</option>
                    <option value="NP">Ne souhaite pas préciser</option>
                  </select>
                </label>
                <label>
                  Nom <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={adhesionForm.lastName}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, lastName: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Prénom <span className="required-marker">*</span>
                  <input
                    type="text"
                    value={adhesionForm.firstName}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, firstName: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Adresse e-mail <span className="required-marker">*</span>
                  <input
                    type="email"
                    value={adhesionForm.email}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, email: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Numéro de téléphone
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={adhesionForm.phone}
                    onChange={(event) =>
                      setAdhesionForm((prev) => ({ ...prev, phone: toDigitsOnly(event.target.value) }))
                    }
                    required
                  />
                </label>
                <label>
                  Ville
                  <input
                    type="text"
                    value={adhesionForm.city}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, city: event.target.value }))}
                  />
                </label>
                <label>
                  Nom et numéro de voie
                  <input
                    type="text"
                    value={adhesionForm.street}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, street: event.target.value }))}
                  />
                </label>
                <label>
                  Code postal
                  <input
                    type="text"
                    value={adhesionForm.postalCode}
                    onChange={(event) => setAdhesionForm((prev) => ({ ...prev, postalCode: event.target.value }))}
                  />
                </label>
              <button type="submit" className="match-result__cta">
                Envoyer la demande
              </button>
            </form>
            </div>
          </div>
        </div>
      )}

      {albumModalOpen && (
        <div className="album-modal" role="dialog" aria-modal="true" aria-label="Album photo">
          <div className="album-modal__content">
            <button
              type="button"
              className="album-modal__close"
              onClick={() => setAlbumModalOpen(false)}
              aria-label="Fermer l’album"
            >
              ×
            </button>
            <h3>Album photo</h3>
            {albumPhotos.length ? (
              <>
                <div className="album-modal__viewer">
                  <button
                    type="button"
                    className="album-modal__nav album-modal__nav--prev"
                    onClick={() => handleAlbumNav('prev')}
                    aria-label="Photo précédente"
                    disabled={albumCount < 2}
                  >
                    ‹
                  </button>
                  <figure>
                    <img src={albumPhotos[albumIndex].src} alt={`Souvenir ${albumIndex + 1}`} />
                  </figure>
                  <button
                    type="button"
                    className="album-modal__nav album-modal__nav--next"
                    onClick={() => handleAlbumNav('next')}
                    aria-label="Photo suivante"
                    disabled={albumCount < 2}
                  >
                    ›
                  </button>
                </div>
                <p className="album-modal__caption">
                  {albumPhotos[albumIndex].title || `Souvenir ${albumIndex + 1}`}
                </p>
                <p className="album-modal__counter">{albumCounter}</p>
              </>
            ) : (
              <p>Aucune photo n’a encore été publiée. Revenez bientôt !</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
